import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../../../hooks/useAuth';
import { subscribeToNotes, createNote, updateNote, deleteNote } from '../services/firestore';
import { FiPlus, FiTrash2, FiSearch, FiX, FiBold, FiItalic, FiUnderline, FiLink, FiImage, FiMinus, FiList, FiZap, FiMessageCircle, FiSend, FiCopy, FiMaximize2, FiMinimize2, FiCheck, FiGlobe, FiStar, FiBook, FiChevronRight, FiEdit3 } from 'react-icons/fi';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';
import EbookPreview from './EbookPreview';

// Rich markdown to HTML renderer (ChatGPT-style)
const renderMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^>(?: (.*))?$/gm, '<div class="chat-blockquote"><p>$1</p></div>')
    .replace(/<\/div>\s*<div class="chat-blockquote">/g, '')
    .replace(/^#{4}\s+(.+)$/gm, '<h4>$1</h4>')
    .replace(/^#{3}\s+(.+)$/gm, '<h4>$1</h4>')
    .replace(/^#{2}\s+(.+)$/gm, '<h3>$1</h3>')
    .replace(/^#{1}\s+(.+)$/gm, '<h2>$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^(\s*)[-*]\s+(.+)$/gm, (_, indent, content) => {
      const cls = indent.length >= 2 ? 'chat-sub-bullet' : 'chat-bullet';
      return `<div class="${cls}"><span class="bullet-dot">•</span><span>${content}</span></div>`;
    })
    .replace(/^\s*(\d+)\.\s+(.+)$/gm, '<div class="chat-numbered"><span class="num">$1.</span><span>$2</span></div>')
    .replace(/(?<![*\w])\*([^*\n]+)\*(?![*\w])/g, '<em>$1</em>')
    .replace(/^---$/gm, '<hr />')
    .replace(/\n{2,}/g, '<div class="chat-separator"></div>')
    .replace(/\n/g, '<br/>');
};

// Convert markdown to editor-friendly HTML (for inserting into note)
const markdownToEditorHtml = (md) => {
  if (!md) return '';
  return md.split('\n').map(line => {
    if (line.trim() === '') return '';
    if (line.trim() === '---') return '<hr style="border:none;border-top:1px solid #e5e7eb;margin:12px 0;" />';
    if (/^#{4}\s+(.+)$/.test(line)) return `<h4>${line.replace(/^#{4}\s+/, '')}</h4>`;
    if (/^#{3}\s+(.+)$/.test(line)) return `<h4>${line.replace(/^#{3}\s+/, '')}</h4>`;
    if (/^#{2}\s+(.+)$/.test(line)) return `<h3>${line.replace(/^#{2}\s+/, '')}</h3>`;
    if (/^#{1}\s+(.+)$/.test(line)) return `<h2>${line.replace(/^#{1}\s+/, '')}</h2>`;
    line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    line = line.replace(/(?<![*\w])\*([^*\n]+)\*(?![*\w])/g, '<em>$1</em>');
    if (/^\s{2,}[-*]\s+/.test(line)) return `<ul style="margin-left:24px;"><li>${line.replace(/^\s+[-*]\s+/, '')}</li></ul>`;
    if (/^[-*]\s+/.test(line)) return `<li>${line.replace(/^[-*]\s+/, '')}</li>`;
    if (/^\s*\d+\.\s/.test(line)) return `<li>${line.replace(/^\s*\d+\.\s+/, '')}</li>`;
    if (/^>\s?(.*)$/.test(line)) return `<blockquote>${line.replace(/^>\s?/, '')}</blockquote>`;
    return `<p>${line}</p>`;
  }).filter(Boolean).join('');
};

const cleanChatHtmlForEditor = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  tmp.querySelectorAll('.chat-bullet, .chat-sub-bullet, [class*="chat-bullet"], [class*="chat-sub-bullet"]').forEach(el => {
    const li = document.createElement('li');
    const spans = el.querySelectorAll('span');
    li.innerHTML = spans.length > 1 ? spans[spans.length - 1].innerHTML : el.textContent;
    if (el.className.includes('sub')) li.style.marginLeft = '24px';
    el.replaceWith(li);
  });
  tmp.querySelectorAll('.chat-numbered, [class*="chat-numbered"]').forEach(el => {
    const li = document.createElement('li');
    const spans = el.querySelectorAll('span');
    li.innerHTML = spans.length > 1 ? spans[spans.length - 1].innerHTML : el.textContent;
    el.replaceWith(li);
  });
  tmp.querySelectorAll('.chat-blockquote, [class*="chat-blockquote"]').forEach(el => {
    const bq = document.createElement('blockquote');
    bq.innerHTML = el.innerHTML;
    el.replaceWith(bq);
  });
  tmp.querySelectorAll('.chat-separator, [class*="chat-separator"]').forEach(el => {
    el.replaceWith(document.createElement('br'));
  });
  tmp.querySelectorAll('*').forEach(el => {
    el.removeAttribute('class');
    const style = el.getAttribute('style');
    if (style && /rgba\(255,\s*255,\s*255/.test(style)) el.removeAttribute('style');
  });
  return tmp.innerHTML;
};

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const NotesView = () => {
  const { user } = useAuth();
  const { selectedProjectId, selectedFolderId, selectedListId, users } = useTask();
  const [notes, setNotes] = useState([]);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Chat
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatImage, setChatImage] = useState(null);
  const [chatWidth, setChatWidth] = useState(45);
  const [streamingText, setStreamingText] = useState(null);

  // Inline AI
  const [selectionPopup, setSelectionPopup] = useState(null);
  const [aiRefineLoading, setAiRefineLoading] = useState(false);
  const savedRangeRef = useRef(null);

  // Ebook
  const [showEbook, setShowEbook] = useState(false);

  const [slashMenu, setSlashMenu] = useState(null); // right-click context menu position
  const [slashFilter, setSlashFilter] = useState('');
  const [colorDropdown, setColorDropdown] = useState(false);
  const [chatSelectionPopup, setChatSelectionPopup] = useState(null);
  const [chatDocs, setChatDocs] = useState([]);
  const [showPersonalize, setShowPersonalize] = useState(false);
  const [personalization, setPersonalization] = useState({ name: '', role: '', preferences: '' });
  const editorCursorRef = useRef(null);
  const contextRangeRef = useRef(null);

  const isDraggingRef = useRef(false);
  const editorRef = useRef(null);
  const saveTimerRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);

  const activeNote = notes.find(n => n.id === activeNoteId);

  // Subscribe to notes
  useEffect(() => {
    if (!user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) {
      setNotes([]); setLoading(false); return;
    }
    setLoading(true);
    let unsub = null;
    subscribeToNotes(user.uid, selectedProjectId, selectedFolderId, selectedListId, (data) => {
      setNotes(data);
      if (!activeNoteId && data.length > 0) {
        setActiveNoteId(data[0].id);
        setNoteTitle(data[0].title || '');
        setNoteContent(data[0].content || '');
      }
      setLoading(false);
    }).then(fn => { unsub = fn; });
    return () => { if (unsub) unsub(); };
  }, [user?.uid, selectedProjectId, selectedFolderId, selectedListId]);

  // Sync editor when note changes
  useEffect(() => {
    if (editorRef.current && activeNote) {
      if (document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = activeNote.content || '';
      }
      setNoteTitle(activeNote.title || '');
      setNoteContent(activeNote.content || '');
    }
  }, [activeNoteId]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading, streamingText]);

  const getUserName = (userId) => {
    const u = users?.find(u => u.id === userId || u.uid === userId);
    return u?.displayName || u?.email?.split('@')[0] || 'Usuario';
  };

  const parseDate = (d) => {
    if (!d) return null;
    if (typeof d === 'object' && d.seconds) return new Date(d.seconds * 1000);
    return new Date(d);
  };

  // ── Note actions ──
  const handleSelectNote = (note) => {
    if (activeNote && (noteContent !== activeNote.content || noteTitle !== activeNote.title)) {
      doSave(activeNote.id, noteTitle, noteContent);
    }
    setActiveNoteId(note.id);
    setNoteTitle(note.title || '');
    setNoteContent(note.content || '');
    if (editorRef.current) editorRef.current.innerHTML = note.content || '';
  };

  const handleCreateNote = async () => {
    if (!user?.uid) return;
    try {
      const id = await createNote(user.uid, selectedProjectId, selectedFolderId, selectedListId, { title: 'Nueva nota', content: '' });
      setActiveNoteId(id);
      setNoteTitle('Nueva nota');
      setNoteContent('');
      if (editorRef.current) editorRef.current.innerHTML = '';
      toast.success('Nota creada');
    } catch { toast.error('Error al crear nota'); }
  };

  const handleDeleteNote = async (noteId) => {
    if (!user?.uid || !window.confirm('Eliminar esta nota?')) return;
    try {
      await deleteNote(user.uid, selectedProjectId, selectedFolderId, selectedListId, noteId);
      if (activeNoteId === noteId) {
        const remaining = notes.filter(n => n.id !== noteId);
        setActiveNoteId(remaining.length > 0 ? remaining[0].id : null);
        if (remaining.length > 0) {
          setNoteTitle(remaining[0].title || '');
          setNoteContent(remaining[0].content || '');
          if (editorRef.current) editorRef.current.innerHTML = remaining[0].content || '';
        } else {
          setNoteTitle(''); setNoteContent('');
          if (editorRef.current) editorRef.current.innerHTML = '';
        }
      }
      toast.success('Nota eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  const doSave = async (noteId, title, content) => {
    if (!noteId || !user?.uid) return;
    setSaving(true);
    try {
      await updateNote(user.uid, selectedProjectId, selectedFolderId, selectedListId, noteId, content, title);
    } catch { /* silent */ }
    setSaving(false);
  };

  const triggerAutoSave = useCallback((title, content) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      if (activeNoteId) doSave(activeNoteId, title, content);
    }, 1200);
  }, [activeNoteId, user?.uid, selectedProjectId, selectedFolderId, selectedListId]);

  const handleTitleChange = (e) => { const val = e.target.value; setNoteTitle(val); triggerAutoSave(val, noteContent); };
  const handleEditorInput = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    setNoteContent(html);
    triggerAutoSave(noteTitle, html);
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current.contains(sel.anchorNode)) {
      editorCursorRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  // ── Editor toolbar ──
  const exec = (cmd, val = null) => {
    const scrollParent = editorRef.current?.closest('.overflow-y-auto');
    const scrollTop = scrollParent?.scrollTop || 0;
    document.execCommand(cmd, false, val);
    editorRef.current?.focus({ preventScroll: true });
    handleEditorInput();
    if (scrollParent) requestAnimationFrame(() => { scrollParent.scrollTop = scrollTop; });
  };
  const insertImage = () => {
    const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0]; if (!file) return;
      if (file.size > 5 * 1024 * 1024) { toast.error('Imagen muy grande (max 5MB)'); return; }
      const reader = new FileReader();
      reader.onload = (ev) => exec('insertHTML', `<img src="${ev.target.result}" style="max-width:100%;border-radius:8px;margin:8px 0;" />`);
      reader.readAsDataURL(file);
    };
    input.click();
  };
  const insertLink = () => { const url = prompt('URL del enlace:'); if (url) exec('createLink', url); };

  const insertHtmlToEditor = (html) => {
    if (!editorRef.current) return;
    const scrollParent = editorRef.current.closest('.overflow-y-auto');
    const scrollTop = scrollParent?.scrollTop || 0;
    editorRef.current.focus({ preventScroll: true });
    const sel = window.getSelection();
    if (editorCursorRef.current) {
      try { sel.removeAllRanges(); sel.addRange(editorCursorRef.current); }
      catch { sel.selectAllChildren(editorRef.current); sel.collapseToEnd(); }
    } else { sel.selectAllChildren(editorRef.current); sel.collapseToEnd(); }
    document.execCommand('insertHTML', false, '<br/>' + html);
    handleEditorInput();
    if (scrollParent) requestAnimationFrame(() => { scrollParent.scrollTop = scrollTop; });
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items; if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const reader = new FileReader();
        reader.onload = (ev) => exec('insertHTML', `<img src="${ev.target.result}" style="max-width:100%;border-radius:8px;margin:8px 0;" />`);
        reader.readAsDataURL(item.getAsFile());
        return;
      }
    }
  };

  // ── Context menu blocks ──
  const CONTEXT_BLOCKS = [
    { id: 'text', label: 'Texto', icon: 'T', action: () => exec('formatBlock', '<p>'), group: 'block' },
    { id: 'h1', label: 'Encabezado 1', icon: 'H1', action: () => exec('formatBlock', '<h1>'), group: 'block' },
    { id: 'h2', label: 'Encabezado 2', icon: 'H2', action: () => exec('formatBlock', '<h2>'), group: 'block' },
    { id: 'h3', label: 'Encabezado 3', icon: 'H3', action: () => exec('formatBlock', '<h3>'), group: 'block' },
    { id: 'h4', label: 'Encabezado 4', icon: 'H4', action: () => exec('formatBlock', '<h4>'), group: 'block' },
    { id: 'bullet', label: 'Lista con viñetas', icon: '•', action: () => exec('insertUnorderedList'), group: 'list' },
    { id: 'numbered', label: 'Lista numerada', icon: '1.', action: () => exec('insertOrderedList'), group: 'list' },
    { id: 'checklist', label: 'Lista de tareas', icon: '☑', action: () => exec('insertHTML', '<div style="display:flex;gap:8px;align-items:flex-start;margin:4px 0;"><input type="checkbox" style="margin-top:3px;accent-color:#6366f1;" /><span>Tarea</span></div>'), group: 'list' },
    { id: 'divider', label: 'Divisor', icon: '—', action: () => exec('insertHTML', '<hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0;" />'), group: 'insert' },
    { id: 'quote', label: 'Cita', icon: '"', action: () => exec('formatBlock', '<blockquote>'), group: 'insert' },
    { id: 'code', label: 'Código', icon: '</>', action: () => exec('insertHTML', '<pre style="background:#f3f4f6;border-radius:8px;padding:12px;font-family:monospace;font-size:13px;margin:8px 0;white-space:pre-wrap;"><code>código aquí</code></pre>'), group: 'insert' },
    { id: 'table', label: 'Tabla', icon: '▦', action: () => exec('insertHTML', '<table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:13px;"><tr><th style="border:1px solid #e5e7eb;padding:8px;background:#f9fafb;text-align:left;">Columna 1</th><th style="border:1px solid #e5e7eb;padding:8px;background:#f9fafb;text-align:left;">Columna 2</th><th style="border:1px solid #e5e7eb;padding:8px;background:#f9fafb;text-align:left;">Columna 3</th></tr><tr><td style="border:1px solid #e5e7eb;padding:8px;"></td><td style="border:1px solid #e5e7eb;padding:8px;"></td><td style="border:1px solid #e5e7eb;padding:8px;"></td></tr><tr><td style="border:1px solid #e5e7eb;padding:8px;"></td><td style="border:1px solid #e5e7eb;padding:8px;"></td><td style="border:1px solid #e5e7eb;padding:8px;"></td></tr></table>'), group: 'insert' },
    { id: 'image', label: 'Imagen', icon: '🖼', action: () => insertImage(), group: 'media' },
    { id: 'paste-plain', label: 'Pegar sin formato', icon: '📋', action: async () => { try { const text = await navigator.clipboard.readText(); if (text) exec('insertText', text); } catch { toast.error('No hay acceso al portapapeles'); } }, group: 'clipboard' },
  ];

  const handleEditorContextMenu = (e) => {
    e.preventDefault();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) contextRangeRef.current = sel.getRangeAt(0).cloneRange();
    const editorRect = editorRef.current.getBoundingClientRect();
    setSlashMenu({ x: e.clientX - editorRect.left, y: e.clientY - editorRect.top });
    setSlashFilter('');
  };

  const executeBlock = (block) => {
    setSlashMenu(null); setSlashFilter('');
    const scrollParent = editorRef.current?.closest('.overflow-y-auto');
    const scrollTop = scrollParent?.scrollTop || 0;
    if (contextRangeRef.current && editorRef.current) {
      editorRef.current.focus({ preventScroll: true });
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(contextRangeRef.current);
    }
    block.action();
    if (scrollParent) requestAnimationFrame(() => { scrollParent.scrollTop = scrollTop; });
  };

  const handleDocUpload = () => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = '.pdf,.txt,.csv,.json,.md,.html,.doc,.docx,image/*';
    inp.multiple = true;
    inp.onchange = async (e) => {
      const files = Array.from(e.target.files);
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} muy grande (max 10MB)`); continue; }
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (ev) => { setChatDocs(prev => [...prev, { name: file.name, type: 'image', content: ev.target.result, size: file.size }]); toast.success(`Imagen ${file.name} cargada`); };
          reader.readAsDataURL(file);
        } else {
          try {
            const text = await file.text();
            setChatDocs(prev => [...prev, { name: file.name, type: 'text', content: text.substring(0, 15000), size: file.size }]);
            toast.success(`${file.name} cargado`);
          } catch { toast.error(`No se pudo leer ${file.name}`); }
        }
      }
    };
    inp.click();
  };

  // ── Inline AI ──
  const handleEditorMouseUp = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) { setSelectionPopup(null); return; }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    savedRangeRef.current = range.cloneRange();
    setSelectionPopup({ x: rect.left + rect.width / 2, y: rect.top - 8, text: sel.toString().trim() });
  };

  const refineSelectedText = async (instruction) => {
    if (!selectionPopup?.text) return;
    setAiRefineLoading(true);
    try {
      const { refineContent } = await import('../../../services/gemini');
      const result = await refineContent(selectionPopup.text, instruction);
      const cleanText = result.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').replace(/^#{1,3}\s+/gm, '').replace(/^[-*]\s+/gm, '• ').trim();
      if (savedRangeRef.current && editorRef.current) {
        editorRef.current.focus();
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
        document.execCommand('insertText', false, '');
        // Typewriter
        setAiRefineLoading(false);
        setSelectionPopup(null);
        savedRangeRef.current = null;
        const chars = cleanText.split('');
        let i = 0;
        const typeChar = () => {
          if (i < chars.length) {
            document.execCommand('insertText', false, chars[i]);
            i++;
            setTimeout(typeChar, chars[i - 1] === '\n' ? 30 : (i % 3 === 0 ? 15 : 8));
          } else { handleEditorInput(); }
        };
        typeChar();
        return;
      }
    } catch (err) { toast.error('Error: ' + err.message); }
    setAiRefineLoading(false);
    setSelectionPopup(null);
    savedRangeRef.current = null;
  };

  // ── Chat IA ──
  const sendChatMessage = async (messageText) => {
    if (!messageText?.trim() || chatLoading) return;
    const userMsg = messageText.trim();
    setChatInput('');
    const currentImage = chatImage;
    setChatImage(null);
    setChatLoading(true);

    setChatMessages(prev => [...prev, { role: 'user', content: userMsg, hasImage: !!currentImage }]);

    try {
      const { generateChatResponse, searchWeb, shouldSearchWeb } = await import('../../../services/gemini');
      const history = [...chatMessages, { role: 'user', content: userMsg }].map(m => ({ role: m.role, content: m.content }));

      let webSearchResults = null;
      try {
        const { needsSearch, searchQuery: sq } = await shouldSearchWeb(userMsg);
        if (needsSearch && sq) webSearchResults = await searchWeb(sq);
      } catch { /* skip */ }

      const noteText = stripHtml(noteContent);
      const personCtx = personalization.name || personalization.role || personalization.preferences
        ? `\n\nSOBRE EL USUARIO:\n- Nombre: ${personalization.name || 'No especificado'}\n- Rol: ${personalization.role || 'No especificado'}\n- Preferencias: ${personalization.preferences || 'Ninguna'}\n`
        : '';
      const docsCtx = chatDocs.filter(d => d.type === 'text').length > 0
        ? '\n\nDOCUMENTOS CARGADOS:\n' + chatDocs.filter(d => d.type === 'text').map(d => `--- ${d.name} ---\n${d.content}`).join('\n\n')
        : '';
      const aiSettings = {
        companyName: 'Chat IA',
        customPrompt: `Eres un asistente inteligente tipo ChatGPT. Respondes preguntas, investigas, generas contenido.

El usuario tiene una nota abierta titulada "${noteTitle}" con este contenido como referencia:
---
${noteText.substring(0, 3000)}
---
${personCtx}${docsCtx}
INSTRUCCIONES:
- Responde como ChatGPT: completo, detallado, util y conversacional
- USA FORMATO RICO: **negritas**, ## títulos, ### subtítulos, listas
- USA EMOJIS relevantes
- Responde siempre en español
- Se proactivo: sugiere siguientes pasos`,
      };

      const aiResponse = await generateChatResponse(history, [], aiSettings, currentImage, [], webSearchResults);

      // Typewriter
      setChatLoading(false);
      const words = aiResponse.split(/(\s+)/);
      let displayed = '';
      setStreamingText({ content: '', hasWebSearch: !!webSearchResults });
      for (let i = 0; i < words.length; i++) {
        displayed += words[i];
        setStreamingText(prev => prev ? { ...prev, content: displayed } : null);
        if (words[i].trim()) await new Promise(r => setTimeout(r, 18));
      }
      setStreamingText(null);

      setChatMessages(prev => [...prev, { role: 'assistant', content: aiResponse, hasWebSearch: !!webSearchResults }]);
    } catch (err) {
      console.error('Chat AI error:', err);
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Error al procesar. Intenta de nuevo.' }]);
    }
    setChatLoading(false);
    setTimeout(() => chatInputRef.current?.focus(), 100);
  };

  const clearChat = () => {
    if (!window.confirm('Limpiar historial del chat?')) return;
    setChatMessages([]);
  };

  // Filter notes
  const filteredNotes = useMemo(() => {
    if (!searchQuery.trim()) return notes;
    const q = searchQuery.toLowerCase();
    return notes.filter(n =>
      (n.title || '').toLowerCase().includes(q) || stripHtml(n.content || '').toLowerCase().includes(q)
    );
  }, [notes, searchQuery]);

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="flex h-full bg-white overflow-hidden">
      {/* ══ SIDEBAR ══ */}
      {sidebarOpen && (
        <div className="w-56 border-r border-gray-200 bg-gray-50/50 flex flex-col shrink-0">
          <div className="p-2 border-b border-gray-100 flex items-center justify-between gap-1">
            <div className="flex items-center gap-1.5">
              <button onClick={() => setSidebarOpen(false)} className="p-1 hover:bg-gray-200 rounded text-gray-400"><FiChevronRight size={12} className="rotate-180" /></button>
              <span className="text-[11px] font-semibold text-gray-700">Notas</span>
              <span className="text-[10px] text-gray-400">{notes.length}</span>
            </div>
            <button onClick={handleCreateNote} className="p-1 hover:bg-blue-50 rounded text-blue-600"><FiPlus size={13} /></button>
          </div>
          {/* Search */}
          <div className="px-2 py-1 border-b border-gray-100">
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md px-1.5 py-0.5">
              <FiSearch size={10} className="text-gray-400 shrink-0" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar..."
                className="text-[10px] bg-transparent border-none focus:outline-none flex-1 text-gray-600 placeholder-gray-300" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="text-gray-300 hover:text-gray-500"><FiX size={9} /></button>}
            </div>
          </div>
          {/* Notes list */}
          <div className="flex-1 overflow-y-auto">
            {filteredNotes.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-xs">
                {searchQuery ? 'Sin resultados' : 'Sin notas'}
                {!searchQuery && <button onClick={handleCreateNote} className="mt-2 block mx-auto text-blue-500 text-xs font-medium">Crear nota</button>}
              </div>
            ) : filteredNotes.map(note => {
              const isActive = activeNoteId === note.id;
              const preview = stripHtml(note.content || '').substring(0, 50) || 'Nota vacía';
              const date = parseDate(note.updatedAt);
              return (
                <div key={note.id} onClick={() => handleSelectNote(note)}
                  className={`group px-3 py-2 border-b border-gray-100/50 cursor-pointer transition-colors
                    ${isActive ? 'bg-blue-50 border-l-[3px] border-l-blue-500' : 'hover:bg-white border-l-[3px] border-l-transparent'}`}>
                  <div className="flex items-start justify-between gap-1">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold text-gray-800 truncate">{note.title || preview}</p>
                      <p className="text-[9px] text-gray-400 mt-0.5 line-clamp-1">{note.title ? preview : ''}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        {date && <p className="text-[9px] text-gray-300">{format(date, 'd MMM, HH:mm', { locale: es })}</p>}
                        <span className="text-[9px] text-gray-300">{getUserName(note.lastEditedBy || note.createdBy)}</span>
                      </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteNote(note.id); }}
                      className="p-0.5 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                      <FiTrash2 size={10} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Collapsed sidebar */}
      {!sidebarOpen && (
        <div className="w-10 border-r border-gray-200 bg-gray-50/50 flex flex-col items-center py-2 gap-2 shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-500"><FiChevronRight size={14} /></button>
          <button onClick={handleCreateNote} className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-600"><FiPlus size={14} /></button>
        </div>
      )}

      {/* ══ EDITOR ══ */}
      <div className="flex-1 bg-white flex flex-col min-w-0">
        {activeNote ? (
          <>
            {/* Title bar */}
            <div className="px-5 py-2.5 border-b border-gray-100 flex items-center justify-between gap-3">
              <input type="text" value={noteTitle} onChange={handleTitleChange} placeholder="Titulo de la nota"
                className="text-base font-bold text-gray-800 bg-transparent border-none focus:outline-none flex-1" />
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-[10px] text-gray-400 mr-1">{saving ? 'Guardando...' : 'Auto-guardado'}</span>
                <button onClick={() => setShowEbook(true)}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-medium flex items-center gap-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50">
                  <FiBook size={13} /> Exportar
                </button>
                <button onClick={() => setShowChat(true)}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-medium flex items-center gap-1.5 text-white shadow-sm hover:shadow-md"
                  style={{ background: '#1e293b' }}>
                  <FiMessageCircle size={13} /> Chat IA
                  {chatMessages.length > 0 && <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />}
                </button>
              </div>
            </div>

            {/* Toolbar */}
            <div className="px-3 py-1.5 border-b border-gray-100 flex items-center gap-0.5 flex-wrap bg-gray-50/50">
              <button onClick={() => exec('bold')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Negrita"><FiBold size={13} /></button>
              <button onClick={() => exec('italic')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Cursiva"><FiItalic size={13} /></button>
              <button onClick={() => exec('underline')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Subrayado"><FiUnderline size={13} /></button>
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              <button onClick={() => exec('formatBlock', '<h2>')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors text-[10px] font-bold" title="Titulo">H2</button>
              <button onClick={() => exec('formatBlock', '<h3>')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors text-[10px] font-bold" title="Subtitulo">H3</button>
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              <button onClick={() => exec('insertUnorderedList')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Lista"><FiList size={13} /></button>
              <button onClick={() => exec('insertOrderedList')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors text-[10px] font-medium" title="Lista numerada">1.</button>
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              <button onClick={insertLink} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Enlace"><FiLink size={13} /></button>
              <button onClick={insertImage} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Imagen"><FiImage size={13} /></button>
              <button onClick={() => exec('formatBlock', '<blockquote>')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors text-[12px]" title="Cita">"</button>
              <button onClick={() => exec('insertHTML', '<hr style="border:none;border-top:1px solid #e5e7eb;margin:12px 0;" />')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Linea"><FiMinus size={13} /></button>
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              {/* Color dropdown */}
              <div className="relative">
                <button onClick={() => setColorDropdown(!colorDropdown)} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors flex items-center gap-0.5" title="Color">
                  <span className="text-[10px] font-bold">A</span>
                  <div className="w-3.5 h-1 rounded-sm" style={{ background: 'linear-gradient(90deg, #ea4335, #fbbc04, #34a853, #1a73e8, #9334e6)' }} />
                </button>
                {colorDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 w-[220px]" onMouseLeave={() => setColorDropdown(false)}>
                    <div className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">Color del texto</div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {[
                        { c: 'inherit', label: 'Default', bg: '#374151' },
                        { c: '#6b7280', label: 'Gris', bg: '#6b7280' },
                        { c: '#92400e', label: 'Marrón', bg: '#92400e' },
                        { c: '#ea580c', label: 'Naranja', bg: '#ea580c' },
                        { c: '#ca8a04', label: 'Amarillo', bg: '#ca8a04' },
                        { c: '#16a34a', label: 'Verde', bg: '#16a34a' },
                        { c: '#2563eb', label: 'Azul', bg: '#2563eb' },
                        { c: '#7c3aed', label: 'Morado', bg: '#7c3aed' },
                        { c: '#db2777', label: 'Rosa', bg: '#db2777' },
                        { c: '#dc2626', label: 'Rojo', bg: '#dc2626' },
                      ].map(({ c, label, bg }) => (
                        <button key={c} onClick={() => { exec('foreColor', c === 'inherit' ? '#374151' : c); setColorDropdown(false); }}
                          className="w-6 h-6 rounded-md border border-gray-200 hover:scale-110 transition-transform flex items-center justify-center" title={label}>
                          <span className="font-bold text-[11px]" style={{ color: bg }}>A</span>
                        </button>
                      ))}
                    </div>
                    <div className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">Color de fondo</div>
                    <div className="flex flex-wrap gap-1">
                      {[
                        { c: 'transparent', label: 'Sin fondo', bg: '#fff', border: true },
                        { c: '#f3f4f6', label: 'Gris', bg: '#f3f4f6' },
                        { c: '#fef3c7', label: 'Amarillo', bg: '#fef3c7' },
                        { c: '#fed7aa', label: 'Naranja', bg: '#fed7aa' },
                        { c: '#fecaca', label: 'Rojo', bg: '#fecaca' },
                        { c: '#bbf7d0', label: 'Verde', bg: '#bbf7d0' },
                        { c: '#bfdbfe', label: 'Azul', bg: '#bfdbfe' },
                        { c: '#ddd6fe', label: 'Morado', bg: '#ddd6fe' },
                        { c: '#fbcfe8', label: 'Rosa', bg: '#fbcfe8' },
                        { c: '#d6d3d1', label: 'Marrón', bg: '#d6d3d1' },
                      ].map(({ c, label, bg, border }) => (
                        <button key={c} onClick={() => { exec('hiliteColor', c); setColorDropdown(false); }}
                          className={`w-6 h-6 rounded-md hover:scale-110 transition-transform ${border ? 'border border-gray-300' : ''}`}
                          style={{ backgroundColor: bg }} title={label} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              <button onClick={() => exec('removeFormat')} className="p-1.5 rounded hover:bg-gray-200 text-gray-400 transition-colors" title="Limpiar formato"><FiX size={12} /></button>
            </div>

            {/* Editor + Chat */}
            <div className="flex-1 flex overflow-hidden">
              <div className="flex-1 overflow-y-auto min-w-0 relative" onClick={() => slashMenu && setSlashMenu(null)}>
                <div ref={editorRef} contentEditable onInput={handleEditorInput} onPaste={handlePaste} onMouseUp={handleEditorMouseUp}
                  onContextMenu={handleEditorContextMenu} suppressContentEditableWarning
                  onBlur={() => {
                    const sel = window.getSelection();
                    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
                      editorCursorRef.current = sel.getRangeAt(0).cloneRange();
                    }
                  }}
                  className="min-h-full p-6 text-sm text-gray-700 leading-relaxed focus:outline-none"
                  style={{ wordBreak: 'break-word' }}
                  data-placeholder="Escribe tu nota aqui... Clic derecho para insertar bloques." />

                {/* Right-click context menu */}
                {slashMenu && (
                  <div className="absolute bg-white rounded-xl shadow-2xl border border-gray-200 py-1 z-50 w-[230px] max-h-[400px] overflow-y-auto"
                    style={{ left: Math.min(slashMenu.x, 300), top: slashMenu.y }}
                    onMouseDown={e => e.preventDefault()}>
                    <div className="px-3 py-1.5">
                      <input type="text" value={slashFilter} onChange={e => setSlashFilter(e.target.value.toLowerCase())}
                        placeholder="Buscar bloques..." autoFocus
                        className="w-full text-[12px] px-2 py-1 rounded-md border border-gray-200 focus:outline-none focus:border-indigo-300 bg-gray-50" />
                    </div>
                    <div className="px-3 pt-1 pb-0.5 text-[9px] font-semibold text-gray-400 uppercase">Bloques</div>
                    {CONTEXT_BLOCKS.filter(b => b.group === 'block' && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).map(block => (
                      <button key={block.id} onClick={() => executeBlock(block)}
                        className="w-full px-3 py-1.5 flex items-center gap-2.5 hover:bg-indigo-50 transition-colors text-left">
                        <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-[11px] font-bold text-gray-500 shrink-0">{block.icon}</span>
                        <span className="text-[13px] text-gray-700">{block.label}</span>
                      </button>
                    ))}
                    {CONTEXT_BLOCKS.filter(b => b.group === 'list' && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).length > 0 && (
                      <>
                        <div className="px-3 pt-2 pb-0.5 text-[9px] font-semibold text-gray-400 uppercase border-t border-gray-100 mt-1">Listas</div>
                        {CONTEXT_BLOCKS.filter(b => b.group === 'list' && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).map(block => (
                          <button key={block.id} onClick={() => executeBlock(block)}
                            className="w-full px-3 py-1.5 flex items-center gap-2.5 hover:bg-indigo-50 transition-colors text-left">
                            <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-[11px] font-bold text-gray-500 shrink-0">{block.icon}</span>
                            <span className="text-[13px] text-gray-700">{block.label}</span>
                          </button>
                        ))}
                      </>
                    )}
                    {CONTEXT_BLOCKS.filter(b => (b.group === 'insert' || b.group === 'media') && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).length > 0 && (
                      <>
                        <div className="px-3 pt-2 pb-0.5 text-[9px] font-semibold text-gray-400 uppercase border-t border-gray-100 mt-1">Insertar</div>
                        {CONTEXT_BLOCKS.filter(b => (b.group === 'insert' || b.group === 'media') && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).map(block => (
                          <button key={block.id} onClick={() => executeBlock(block)}
                            className="w-full px-3 py-1.5 flex items-center gap-2.5 hover:bg-indigo-50 transition-colors text-left">
                            <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-[11px] font-bold text-gray-500 shrink-0">{block.icon}</span>
                            <span className="text-[13px] text-gray-700">{block.label}</span>
                          </button>
                        ))}
                      </>
                    )}
                    {CONTEXT_BLOCKS.filter(b => b.group === 'clipboard' && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).length > 0 && (
                      <>
                        <div className="px-3 pt-2 pb-0.5 text-[9px] font-semibold text-gray-400 uppercase border-t border-gray-100 mt-1">Portapapeles</div>
                        {CONTEXT_BLOCKS.filter(b => b.group === 'clipboard' && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).map(block => (
                          <button key={block.id} onClick={() => executeBlock(block)}
                            className="w-full px-3 py-1.5 flex items-center gap-2.5 hover:bg-indigo-50 transition-colors text-left">
                            <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-[11px] font-bold text-gray-500 shrink-0">{block.icon}</span>
                            <span className="text-[13px] text-gray-700">{block.label}</span>
                          </button>
                        ))}
                      </>
                    )}
                    {CONTEXT_BLOCKS.filter(b => !slashFilter || b.label.toLowerCase().includes(slashFilter)).length === 0 && (
                      <div className="px-3 py-3 text-[12px] text-gray-400 text-center">Sin resultados</div>
                    )}
                  </div>
                )}
              </div>

              {/* ══ CHAT IA ══ */}
              {showChat && (
                <>
                  <div style={{ width: `${chatWidth}vw` }} className="shrink-0" />
                  <div className="fixed top-0 right-0 bottom-0 z-[60] flex" style={{ width: `${chatWidth}vw` }}>
                    <div className="w-1.5 shrink-0 cursor-col-resize hover:bg-blue-400 bg-gray-700 transition-colors"
                      onMouseDown={(e) => {
                        e.preventDefault(); isDraggingRef.current = true;
                        const startX = e.clientX; const startWidth = chatWidth;
                        const onMove = (me) => setChatWidth(Math.max(25, Math.min(65, startWidth + (startX - me.clientX) / window.innerWidth * 100)));
                        const onUp = () => { isDraggingRef.current = false; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                        document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
                      }} />
                    <div className="flex-1 flex flex-col overflow-hidden min-h-0" style={{ background: '#212121' }}>
                      {/* Header */}
                      <div className="px-4 py-2.5 flex items-center justify-between shrink-0">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center"><FiZap size={14} className="text-white" /></div>
                          <span className="text-[13px] font-medium text-white/90">HingenIA Chat</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {chatMessages.length > 0 && (
                            <button onClick={clearChat} className="p-1.5 rounded-lg text-white/40 hover:text-red-400 hover:bg-white/10"><FiTrash2 size={13} /></button>
                          )}
                          <button onClick={() => setShowPersonalize(true)} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors relative" title="Cerebro del Asistente">
                            <span className="text-[14px]">🧠</span>
                            {(chatDocs.length > 0 || personalization.preferences) && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-500 rounded-full" />}
                          </button>
                          <button onClick={() => setShowChat(false)} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10"><FiX size={15} /></button>
                        </div>
                      </div>

                      {/* Messages */}
                      <div className="flex-1 overflow-y-auto px-4 relative" onMouseUp={() => {
                        const sel = window.getSelection();
                        if (!sel || sel.isCollapsed || !sel.toString().trim()) { setChatSelectionPopup(null); return; }
                        const text = sel.toString().trim();
                        if (text.length < 3) { setChatSelectionPopup(null); return; }
                        const range = sel.getRangeAt(0);
                        const rect = range.getBoundingClientRect();
                        const frag = range.cloneContents();
                        const tmp = document.createElement('div');
                        tmp.appendChild(frag);
                        setChatSelectionPopup({ x: rect.left + rect.width / 2, y: rect.top - 8, text, html: tmp.innerHTML });
                      }} onClick={() => { if (!window.getSelection()?.toString().trim()) setChatSelectionPopup(null); }}>
                        {/* Chat selection popup */}
                        {chatSelectionPopup && (
                          <div className="fixed z-[80] flex items-center gap-1 bg-[#1a1a2e] border border-white/15 rounded-xl shadow-2xl px-1.5 py-1"
                            style={{ left: Math.min(chatSelectionPopup.x - 80, window.innerWidth - 200), top: chatSelectionPopup.y - 40 }}
                            onMouseDown={e => e.preventDefault()}>
                            <button onClick={() => {
                              insertHtmlToEditor(cleanChatHtmlForEditor(chatSelectionPopup.html));
                              toast.success('Insertado en la nota');
                              setChatSelectionPopup(null);
                              window.getSelection()?.removeAllRanges();
                            }} className="px-2.5 py-1.5 text-[11px] text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-1.5">
                              <FiPlus size={11} /> Insertar
                            </button>
                            <div className="w-px h-4 bg-white/10" />
                            <button onClick={() => {
                              const cleaned = cleanChatHtmlForEditor(chatSelectionPopup.html);
                              const blob = new Blob([cleaned], { type: 'text/html' });
                              const textBlob = new Blob([chatSelectionPopup.text], { type: 'text/plain' });
                              navigator.clipboard.write([new ClipboardItem({ 'text/html': blob, 'text/plain': textBlob })]).then(() => {
                                toast.success('Copiado con formato');
                              }).catch(() => { navigator.clipboard.writeText(chatSelectionPopup.text); toast.success('Copiado'); });
                              setChatSelectionPopup(null);
                              window.getSelection()?.removeAllRanges();
                            }} className="px-2.5 py-1.5 text-[11px] text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-1.5">
                              <FiCopy size={11} /> Copiar
                            </button>
                          </div>
                        )}
                        <div className="max-w-full mx-auto py-4 space-y-6 px-2">
                          {chatMessages.length === 0 && !chatLoading && !streamingText && (
                            <div className="text-center py-24">
                              <div className="w-12 h-12 rounded-full bg-white/10 mx-auto mb-4 flex items-center justify-center"><FiZap size={22} className="text-white/60" /></div>
                              <h4 className="text-lg font-medium text-white/80">Como puedo ayudarte?</h4>
                              <p className="text-sm text-white/30 mt-2">Pregunta lo que quieras sobre esta nota.</p>
                            </div>
                          )}

                          {chatMessages.map((msg, i) => (
                            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : ''}>
                              {msg.role === 'assistant' ? (
                                <div className="flex gap-3">
                                  <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5"><FiZap size={13} className="text-white/70" /></div>
                                  <div className="flex-1 min-w-0">
                                    {msg.hasWebSearch && <div className="text-[10px] text-blue-400 mb-2 flex items-center gap-1"><FiGlobe size={10} /> Busqué en internet</div>}
                                    <div className="text-[14px] text-white/85 leading-relaxed chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                                    <div className="flex gap-3 mt-3">
                                      <button onClick={() => {
                                        insertHtmlToEditor(markdownToEditorHtml(msg.content));
                                        toast.success('Insertado en la nota');
                                      }} className="text-[11px] text-white/30 hover:text-white/60 flex items-center gap-1"><FiPlus size={11} /> Insertar</button>
                                      <button onClick={() => { navigator.clipboard.writeText(msg.content); toast.success('Copiado'); }}
                                        className="text-[11px] text-white/30 hover:text-white/60 flex items-center gap-1"><FiCopy size={11} /> Copiar</button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="inline-block max-w-[80%] bg-[#2f2f2f] text-white/90 rounded-2xl px-4 py-2.5 text-[14px]">{msg.content}</div>
                              )}
                            </div>
                          ))}

                          {/* Streaming */}
                          {streamingText && (
                            <div className="flex gap-3">
                              <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5"><FiZap size={13} className="text-white/70" /></div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[14px] text-white/85 leading-relaxed chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(streamingText.content) }} />
                                <span className="inline-block w-0.5 h-4 bg-white/60 animate-pulse ml-0.5 align-middle" />
                              </div>
                            </div>
                          )}

                          {chatLoading && !streamingText && (
                            <div className="flex gap-3">
                              <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0"><FiZap size={13} className="text-white/70" /></div>
                              <div className="flex gap-1 py-2">
                                <div className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" />
                                <div className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                              </div>
                            </div>
                          )}
                          <div ref={chatEndRef} />
                        </div>
                      </div>

                      {/* Input */}
                      <div className="px-4 pb-4 pt-2 shrink-0">
                        {/* Docs indicator */}
                        {chatDocs.length > 0 && (
                          <div className="mb-2 flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 rounded-xl px-3 py-1.5">
                            <span className="text-[12px]">🧠</span>
                            <span className="text-[11px] text-indigo-300/70 flex-1">{chatDocs.length} documento{chatDocs.length > 1 ? 's' : ''} en memoria</span>
                            <button onClick={() => setShowPersonalize(true)} className="text-[10px] text-indigo-400 hover:text-indigo-300">Gestionar</button>
                          </div>
                        )}
                        {chatImage && (
                          <div className="mb-2 flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2">
                            <img src={chatImage} alt="" className="w-10 h-10 rounded-lg object-cover" />
                            <span className="text-[11px] text-white/40 flex-1">Imagen lista</span>
                            <button onClick={() => setChatImage(null)} className="text-white/30 hover:text-white/60"><FiX size={14} /></button>
                          </div>
                        )}
                        <div className="flex items-end gap-2 bg-[#2f2f2f] rounded-2xl border border-white/10 px-3 py-2">
                          <button onClick={() => {
                            const inp = document.createElement('input'); inp.type = 'file'; inp.accept = 'image/*';
                            inp.onchange = (e) => { const file = e.target.files[0]; if (!file) return; const reader = new FileReader(); reader.onload = (ev) => setChatImage(ev.target.result); reader.readAsDataURL(file); };
                            inp.click();
                          }} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors shrink-0" title="Adjuntar imagen">
                            <FiImage size={16} />
                          </button>
                          <input ref={chatInputRef} value={chatInput} onChange={e => setChatInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(chatInput); } }}
                            placeholder="Pregunta lo que quieras..."
                            className="flex-1 bg-transparent text-[14px] text-white/90 placeholder-white/25 py-1.5 focus:outline-none"
                            disabled={chatLoading} autoFocus />
                          <button onClick={() => sendChatMessage(chatInput)} disabled={chatLoading || !chatInput.trim()}
                            className="p-1.5 rounded-lg transition-all shrink-0"
                            style={{ background: chatInput.trim() && !chatLoading ? 'white' : 'transparent', color: chatInput.trim() && !chatLoading ? '#212121' : 'rgba(255,255,255,0.2)' }}>
                            <FiSend size={16} />
                          </button>
                        </div>
                        <p className="text-[9px] text-white/15 text-center mt-2">HingenIA</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* ══ TEXT SELECTION POPUP ══ */}
            {selectionPopup && !aiRefineLoading && (
              <div className="fixed z-[90]"
                onMouseDown={e => { if (e.target.tagName !== 'INPUT') e.preventDefault(); }}
                style={{ left: Math.min(selectionPopup.x - 155, window.innerWidth - 330), top: selectionPopup.y - 48 }}>
                <div className="flex items-center bg-gray-800 rounded-full shadow-xl border border-gray-700/60 px-1 py-0.5 gap-0.5">
                  <div className="flex items-center flex-1 min-w-0">
                    <input type="text" placeholder="Editar o explicar..."
                      className="bg-transparent text-[12px] text-white/90 placeholder-white/35 px-3 py-1.5 focus:outline-none w-[160px]"
                      onMouseDown={e => e.stopPropagation()}
                      onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) refineSelectedText(e.target.value.trim()); if (e.key === 'Escape') { setSelectionPopup(null); savedRangeRef.current = null; } }} />
                    <button onMouseDown={e => e.stopPropagation()}
                      onClick={e => { const input = e.target.closest('div')?.parentElement?.querySelector('input'); if (input?.value.trim()) refineSelectedText(input.value.trim()); }}
                      className="p-1 text-white/40 hover:text-white/80 transition-colors shrink-0 mr-0.5"><FiSend size={12} /></button>
                  </div>
                  <div className="w-px h-5 bg-white/15" />
                  {[
                    { icon: <FiZap size={13} />, label: 'Mejorar', prompt: 'Mejora la redacción para que sea más clara y profesional.' },
                    { icon: <FiMaximize2 size={13} />, label: 'Expandir', prompt: 'Expande este texto con más detalle y ejemplos.' },
                    { icon: <FiCheck size={13} />, label: 'Corregir', prompt: 'Corrige errores ortográficos y gramaticales.' },
                  ].map(a => (
                    <button key={a.label} onClick={() => refineSelectedText(a.prompt)} title={a.label}
                      className="p-1.5 rounded-full hover:bg-white/15 transition-colors text-white/50 hover:text-white">{a.icon}</button>
                  ))}
                  <button onClick={() => { setSelectionPopup(null); savedRangeRef.current = null; }} className="p-1 text-white/30 hover:text-white/60"><FiX size={12} /></button>
                </div>
              </div>
            )}
            {aiRefineLoading && (
              <div className="fixed z-[90] bg-gray-800 text-white rounded-full shadow-xl px-4 py-2 flex items-center gap-2 border border-gray-700/60"
                style={{ left: selectionPopup?.x - 60 || 200, top: (selectionPopup?.y || 200) - 44 }}>
                <div className="w-3 h-3 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                <span className="text-[12px] text-white/70">Procesando...</span>
              </div>
            )}

            {/* Brain Modal */}
            {showPersonalize && (
              <div className="fixed inset-0 z-[110] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setShowPersonalize(false)}>
                <div className="w-[480px] max-w-[92vw] max-h-[85vh] bg-[#1e1e1e] rounded-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                  <div className="px-5 py-4 border-b border-white/8 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-lg">🧠</div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-white/90">Cerebro del Asistente</h3>
                      <p className="text-[10px] text-white/35">Personaliza tu IA con instrucciones y conocimiento</p>
                    </div>
                    <button onClick={() => setShowPersonalize(false)} className="p-1.5 text-white/30 hover:text-white/70 rounded-lg hover:bg-white/5"><FiX size={16} /></button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-[11px] font-semibold text-white/50 uppercase"><span>👤</span> Sobre ti</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[10px] text-white/40 mb-1 block">Nombre</label>
                          <input value={personalization.name} onChange={e => setPersonalization(p => ({ ...p, name: e.target.value }))} placeholder="Tu nombre"
                            className="w-full text-[13px] bg-white/5 border border-white/8 rounded-xl px-3 py-2 text-white/80 placeholder-white/20 focus:outline-none focus:border-indigo-500/50" />
                        </div>
                        <div>
                          <label className="text-[10px] text-white/40 mb-1 block">Rol</label>
                          <input value={personalization.role} onChange={e => setPersonalization(p => ({ ...p, role: e.target.value }))} placeholder="Ej: CEO, Vendedor..."
                            className="w-full text-[13px] bg-white/5 border border-white/8 rounded-xl px-3 py-2 text-white/80 placeholder-white/20 focus:outline-none focus:border-indigo-500/50" />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-[11px] font-semibold text-white/50 uppercase"><span>⚡</span> Instrucciones</div>
                      <textarea value={personalization.preferences} onChange={e => setPersonalization(p => ({ ...p, preferences: e.target.value }))}
                        placeholder="Ej: Eres un experto en ventas..." rows={4}
                        className="w-full text-[13px] bg-white/5 border border-white/8 rounded-xl px-3 py-2.5 text-white/80 placeholder-white/20 focus:outline-none focus:border-indigo-500/50 resize-none" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[11px] font-semibold text-white/50 uppercase"><span>📚</span> Base de Conocimiento</div>
                        <button onClick={handleDocUpload} className="text-[10px] text-indigo-400 hover:text-indigo-300 flex items-center gap-1 font-medium"><FiPlus size={11} /> Subir</button>
                      </div>
                      {chatDocs.length === 0 ? (
                        <button onClick={handleDocUpload} className="w-full py-6 border-2 border-dashed border-white/10 rounded-xl text-white/25 hover:text-white/50 hover:border-white/20 transition-colors flex flex-col items-center gap-2">
                          <FiPlus size={20} /><span className="text-[11px]">Subir archivos</span>
                        </button>
                      ) : (
                        <div className="space-y-1.5">
                          {chatDocs.map((d, i) => (
                            <div key={i} className="flex items-center gap-2 bg-white/5 border border-white/8 rounded-lg px-3 py-2">
                              <span className="text-sm">{d.type === 'image' ? '🖼' : '📄'}</span>
                              <div className="flex-1 min-w-0">
                                <div className="text-[12px] text-white/70 truncate">{d.name}</div>
                                <div className="text-[9px] text-white/30">{(d.size / 1024).toFixed(0)} KB</div>
                              </div>
                              <button onClick={() => setChatDocs(prev => prev.filter((_, j) => j !== i))} className="p-1 text-white/20 hover:text-red-400"><FiX size={12} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="px-5 py-4 border-t border-white/8 flex items-center gap-3">
                    <div className="flex-1" />
                    <button onClick={() => setShowPersonalize(false)} className="px-4 py-2 text-[12px] text-white/50 hover:text-white/80">Cancelar</button>
                    <button onClick={() => setShowPersonalize(false)} className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-[12px] font-medium hover:opacity-90">Guardar</button>
                  </div>
                </div>
              </div>
            )}

            {/* Ebook Preview */}
            <EbookPreview isOpen={showEbook} onClose={() => setShowEbook(false)} title={noteTitle} content={noteContent} authorName={getUserName(user?.uid)} />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <FiEdit3 size={40} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">Selecciona o crea una nota</p>
              <button onClick={handleCreateNote} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
                <FiPlus size={12} className="inline mr-1" /> Nueva nota
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesView;
